import { useState } from 'react';
import { deleteOrgFile, writeOrgFile } from '../../storage/FileSystemAdapter';
import { appendChangeLog } from '../../utils/changeLog';
import { scanReferencesToEntity } from '../../utils/intelligence';
import { MS_PER_DAY } from '../../utils/constants';
import { CRITICALITY_LEVELS, SUPPLIER_TYPES, SUPPLIER_STATUSES } from '../../utils/enums';
import Badge from '../shared/Badge';
import { BG_CONTENT, BTN_SECONDARY, BTN_DESTRUCTIVE_OUTLINE, BTN_DESTRUCTIVE_FILLED, BTN_STATUS_FORWARD, BTN_STATUS_FLAG, TEXT_MUTED, TEXT_DISABLED, BORDER_DEFAULT, TEXT_HEADING, TEXT_DEFAULT, BG_SURFACE, BRAND_PRIMARY_DARK, ERROR_TEXT, ERROR_BG, ERROR_BORDER, BG_MUTED, STATUS_GOOD, BG_SHELL, STATUS_BAD, STATUS_WARN, UNSAVED_AMBER, SURFACE_WHITE, FONT_BODY, FONT_LABEL, FONT_SMALL, FONT_TINY, FONT_LIST_ITEM } from '../../theme';
import ConfirmDialog from '../shared/ConfirmDialog';
import { dateStatus } from '../../utils/dateStatus';

// ─── §11 RULE 42 — BLOCK DIALOG SUPPORT ──────────────────────────────────────
const HOLDER_NAV = {
  'risk': 'risks', 'asset': 'assets', 'supplier': 'suppliers',
  'incident': 'incidents', 'audit': 'audits', 'nonconformity': 'nonconformities',
  'objective': 'objectives', 'metric': 'metrics', 'document': 'documents',
  'legal-register': 'legal-register', 'management-review': 'management-reviews',
  'soa-control': 'soa', 'scope': 'scope',
};
function holderLabel(t, holderType, count) {
  const key = `deleteBlockedHolder_${holderType}_${count === 1 ? 'one' : 'other'}`;
  return t[key] || holderType;
}
function summariseHits(hits, t) {
  const counts = {};
  hits.forEach(h => { counts[h.holderType] = (counts[h.holderType] || 0) + 1; });
  return Object.entries(counts).map(([type, n]) => `${n} ${holderLabel(t, type, n)}`).join(', ');
}

// ── CONSTANTS ─────────────────────────────────────────────────────────────────
const CRITICALITIES  = CRITICALITY_LEVELS;

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    newSupplier:           '+ Nuevo proveedor',
    loading:               'Cargando proveedores…',
    emptyTitle:            'No hay proveedores registrados.',
    emptyHint:             'Crea el primer proveedor con el botón "+ Nuevo proveedor".',
    colRef:                'Referencia',
    colName:               'Nombre',
    colType:               'Tipo',
    colCriticality:        'Criticidad',
    colContractExpiry:     'Vto. contrato',
    colStatus:             'Estado',
    colActions:            'Acciones',
    filterAll:             'Todos',
    filterName:            'Nombre',
    filterNamePh:          'Buscar…',
    filterType:            'Tipo',
    filterCriticality:     'Criticidad',
    filterStatus:          'Estado',
    filterExpiry:          'Estado del contrato',
    expiryAll:             'Todos',
    expiryExpired:         'Vencido',
    expirySoon:            'Próximo a vencer (<90 días)',
    expiryActive:          'Activo (>90 días)',
    expiryNone:            'Sin fecha',
    viewBtn:               'Ver',
    editBtn:               'Editar',
    flagReviewBtn:         'Marcar revisión',
    markActiveBtn:         'Marcar activo',
    reactivateBtn:         'Reactivar',
    deleteBtn:             'Eliminar',
    deleteConfirm:         '¿Eliminar el proveedor "{name}"? Esta acción no se puede deshacer.',
    selectAll:             'Seleccionar todo',
    nSelected:             '{n} seleccionados',
    deleteSelected:        'Eliminar seleccionados',
    deleteSelectedConfirm: '¿Eliminar los siguientes proveedores?\n\n{names}\n\nEsta acción no se puede deshacer.',
    noExpiry:              '—',
    saveError:             'Error al guardar. Inténtalo de nuevo.',
    saving:                'Guardando…',
    typeLabels: {
      Cloud:                   'Cloud',
      SaaS:                    'SaaS',
      'IT Services':           'Servicios TI',
      'Professional Services': 'Servicios profesionales',
      Hardware:                'Hardware',
      Other:                   'Otro',
    },
    criticalityLabels: {
      Critical: 'Crítico',
      High:     'Alta',
      Medium:   'Media',
      Low:      'Baja',
    },
    statusLabels: {
      'Active':       'Activo',
      'Under Review': 'En revisión',
      'Terminated':   'Terminado',
    },
    viewTitle:             'Ver proveedor',
    backToList:            '← Volver al listado',
    vRef:                  'Referencia',
    vName:                 'Nombre',
    vType:                 'Tipo',
    vCriticality:          'Criticidad',
    vDescription:          'Descripción',
    vAddress:              'Dirección',
    vContactName:          'Nombre de contacto',
    vContactEmail:         'Email de contacto',
    vOwner:                'Responsable interno',
    vServicesProvided:     'Servicios proporcionados',
    vDataAccess:           'Acceso a datos',
    vDataTypes:            'Tipos de datos',
    vSecurityReq:          'Requisitos de seguridad',
    vContractRef:          'Ref. contrato',
    vContractStart:        'Inicio contrato',
    vContractExpiry:       'Vto. contrato',
    vReviewFrequency:      'Frecuencia de revisión (meses)',
    vLastReviewDate:       'Última revisión',
    vLastReviewOutcome:    'Resultado última revisión',
    vNextReviewDate:       'Próxima revisión',
    vRisks:                'Riesgos vinculados',
    vControls:             'Controles del Anexo A',
    vEvidence:             'Evidencias vinculadas',
    vStatus:               'Estado',
    vNotes:                'Notas',
    vYes:                  'Sí',
    vNo:                   'No',
    vEmpty:                '—',
    badgeOverdue:          'Vencido',
    badgeSoon:             'Vence pronto',
    formCancel:            'Cancelar',
    deleteBlockedTitle:        'No se puede eliminar: existen referencias',
    deleteBlockedReferencedBy: 'Referenciado por',
    deleteBlockedOpenHolder:   'Abrir',
    deactivateInstead:         'Desactivar en su lugar',
    bulkDeleteSummary_allBlocked: 'Ninguno de los registros seleccionados puede eliminarse porque todos están referenciados por otros registros.',
    bulkDeleteSummary_mixed:      '{deletable} de {total} registros pueden eliminarse; {blocked} están bloqueados por referencias.',
    continueWithDeletables:       'Eliminar los registros no bloqueados',
    deleteBlockedHolder_risk_one:               'riesgo',
    deleteBlockedHolder_risk_other:             'riesgos',
    deleteBlockedHolder_asset_one:              'activo',
    deleteBlockedHolder_asset_other:            'activos',
    deleteBlockedHolder_supplier_one:           'proveedor',
    deleteBlockedHolder_supplier_other:         'proveedores',
    deleteBlockedHolder_incident_one:           'incidente',
    deleteBlockedHolder_incident_other:         'incidentes',
    deleteBlockedHolder_audit_one:              'auditoría',
    deleteBlockedHolder_audit_other:            'auditorías',
    deleteBlockedHolder_nonconformity_one:      'no conformidad',
    deleteBlockedHolder_nonconformity_other:    'no conformidades',
    deleteBlockedHolder_objective_one:          'objetivo',
    deleteBlockedHolder_objective_other:        'objetivos',
    deleteBlockedHolder_metric_one:             'métrica',
    deleteBlockedHolder_metric_other:           'métricas',
    deleteBlockedHolder_document_one:           'documento',
    deleteBlockedHolder_document_other:         'documentos',
    'deleteBlockedHolder_legal-register_one':   'entrada del registro legal',
    'deleteBlockedHolder_legal-register_other': 'entradas del registro legal',
    'deleteBlockedHolder_management-review_one':   'revisión por la dirección',
    'deleteBlockedHolder_management-review_other': 'revisiones por la dirección',
    'deleteBlockedHolder_soa-control_one':      'control del SoA',
    'deleteBlockedHolder_soa-control_other':    'controles del SoA',
    deleteBlockedHolder_scope_one:              'alcance',
    deleteBlockedHolder_scope_other:            'alcance',
  },
  en: {
    newSupplier:           '+ New supplier',
    loading:               'Loading suppliers…',
    emptyTitle:            'No suppliers recorded yet.',
    emptyHint:             'Create the first supplier using the "+ New supplier" button.',
    colRef:                'Reference',
    colName:               'Name',
    colType:               'Type',
    colCriticality:        'Criticality',
    colContractExpiry:     'Contract expiry',
    colStatus:             'Status',
    colActions:            'Actions',
    filterAll:             'All',
    filterName:            'Name',
    filterNamePh:          'Search…',
    filterType:            'Type',
    filterCriticality:     'Criticality',
    filterStatus:          'Status',
    filterExpiry:          'Contract expiry status',
    expiryAll:             'All',
    expiryExpired:         'Expired',
    expirySoon:            'Expiring soon (<90 days)',
    expiryActive:          'Active (>90 days)',
    expiryNone:            'No expiry',
    viewBtn:               'View',
    editBtn:               'Edit',
    flagReviewBtn:         'Flag for review',
    markActiveBtn:         'Mark active',
    reactivateBtn:         'Reactivate',
    deleteBtn:             'Delete',
    deleteConfirm:         'Delete supplier "{name}"? This cannot be undone.',
    selectAll:             'Select all',
    nSelected:             '{n} selected',
    deleteSelected:        'Delete selected',
    deleteSelectedConfirm: 'Delete the following suppliers?\n\n{names}\n\nThis cannot be undone.',
    noExpiry:              '—',
    saveError:             'Could not save. Please try again.',
    saving:                'Saving…',
    typeLabels: {
      Cloud:                   'Cloud',
      SaaS:                    'SaaS',
      'IT Services':           'IT Services',
      'Professional Services': 'Professional Services',
      Hardware:                'Hardware',
      Other:                   'Other',
    },
    criticalityLabels: {
      Critical: 'Critical',
      High:     'High',
      Medium:   'Medium',
      Low:      'Low',
    },
    statusLabels: {
      'Active':       'Active',
      'Under Review': 'Under Review',
      'Terminated':   'Terminated',
    },
    viewTitle:             'View supplier',
    backToList:            '← Back to list',
    vRef:                  'Reference',
    vName:                 'Name',
    vType:                 'Type',
    vCriticality:          'Criticality',
    vDescription:          'Description',
    vAddress:              'Address',
    vContactName:          'Contact name',
    vContactEmail:         'Contact email',
    vOwner:                'Internal responsible',
    vServicesProvided:     'Services provided',
    vDataAccess:           'Data access',
    vDataTypes:            'Types of data',
    vSecurityReq:          'Security requirements',
    vContractRef:          'Contract reference',
    vContractStart:        'Contract start',
    vContractExpiry:       'Contract expiry',
    vReviewFrequency:      'Review frequency (months)',
    vLastReviewDate:       'Last review date',
    vLastReviewOutcome:    'Last review outcome',
    vNextReviewDate:       'Next review',
    vRisks:                'Linked risks',
    vControls:             'Annex A controls',
    vEvidence:             'Linked evidence',
    vStatus:               'Status',
    vNotes:                'Notes',
    vYes:                  'Yes',
    vNo:                   'No',
    vEmpty:                '—',
    badgeOverdue:          'Overdue',
    badgeSoon:             'Due soon',
    formCancel:            'Cancel',
    deleteBlockedTitle:        'Cannot delete: references found',
    deleteBlockedReferencedBy: 'Referenced by',
    deleteBlockedOpenHolder:   'Open',
    deactivateInstead:         'Deactivate instead',
    bulkDeleteSummary_allBlocked: 'None of the selected records can be deleted because all of them are referenced by other records.',
    bulkDeleteSummary_mixed:      '{deletable} of {total} records can be deleted; {blocked} are blocked by references.',
    continueWithDeletables:       'Delete the unblocked records',
    deleteBlockedHolder_risk_one:               'risk',
    deleteBlockedHolder_risk_other:             'risks',
    deleteBlockedHolder_asset_one:              'asset',
    deleteBlockedHolder_asset_other:            'assets',
    deleteBlockedHolder_supplier_one:           'supplier',
    deleteBlockedHolder_supplier_other:         'suppliers',
    deleteBlockedHolder_incident_one:           'incident',
    deleteBlockedHolder_incident_other:         'incidents',
    deleteBlockedHolder_audit_one:              'audit',
    deleteBlockedHolder_audit_other:            'audits',
    deleteBlockedHolder_nonconformity_one:      'nonconformity',
    deleteBlockedHolder_nonconformity_other:    'nonconformities',
    deleteBlockedHolder_objective_one:          'objective',
    deleteBlockedHolder_objective_other:        'objectives',
    deleteBlockedHolder_metric_one:             'metric',
    deleteBlockedHolder_metric_other:           'metrics',
    deleteBlockedHolder_document_one:           'document',
    deleteBlockedHolder_document_other:         'documents',
    'deleteBlockedHolder_legal-register_one':   'legal register entry',
    'deleteBlockedHolder_legal-register_other': 'legal register entries',
    'deleteBlockedHolder_management-review_one':   'management review',
    'deleteBlockedHolder_management-review_other': 'management reviews',
    'deleteBlockedHolder_soa-control_one':      'SoA control',
    'deleteBlockedHolder_soa-control_other':    'SoA controls',
    deleteBlockedHolder_scope_one:              'scope',
    deleteBlockedHolder_scope_other:            'scope',
  },
};

// ── COLOR MAPS ────────────────────────────────────────────────────────────────
const CRITICALITY_COLORS = {
  High:   { bg: ERROR_BG, text: ERROR_TEXT },
  Medium: { bg: STATUS_WARN.bg, text: STATUS_WARN.text },
  Low:    { bg: STATUS_GOOD.bg, text: BRAND_PRIMARY_DARK },
};

const STATUS_COLORS = {
  'Active':       { bg: STATUS_GOOD.bg, text: BRAND_PRIMARY_DARK },
  'Under Review': { bg: STATUS_WARN.bg, text: STATUS_WARN.text },
  'Terminated':   { bg: BG_MUTED, text: TEXT_MUTED },
};

function expiryStyle(dateStr) {
  if (!dateStr) return {};
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(dateStr);
  const diffDays = Math.floor((expiry - today) / MS_PER_DAY);
  if (diffDays < 0) return { color: ERROR_TEXT, fontWeight: 600 };
  if (diffDays <= 30) return { color: UNSAVED_AMBER, fontWeight: 600 };
  return {};
}

// ── COMPONENT ─────────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
export default function SupplierList({ suppliers, loading, org, lang, onNew, onEdit, onView, onNavigate, onSuppliersChanged }) {
  const t = T[lang] || T.en;

  const [filterText,        setFilterText]        = useState('');
  const [filterType,        setFilterType]        = useState('');
  const [filterCriticality, setFilterCriticality] = useState('');
  const [filterStatus,      setFilterStatus]      = useState('');
  const [filterExpiry,      setFilterExpiry]      = useState('');
  const [selectedIds,       setSelectedIds]       = useState(new Set());
  const [listError,         setListError]         = useState('');
  const [savingId,          setSavingId]          = useState(null);
  const [confirmState,      setConfirmState]      = useState(null);
  const [scanningDelete,    setScanningDelete]    = useState(false);

  const showError = (msg) => { setListError(msg); setTimeout(() => setListError(''), 4000); };

  const safeSuppliers = Array.isArray(suppliers) ? suppliers : [];

  // Classify contract expiry into buckets
  const expiryBucket = (dateStr) => {
    if (!dateStr) return 'none';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const exp = new Date(dateStr);
    const diff = Math.floor((exp - today) / MS_PER_DAY);
    if (diff < 0) return 'expired';
    if (diff < 90) return 'soon';
    return 'active';
  };

  const filtered = safeSuppliers.filter((s) => {
    if (filterText) {
      const q = filterText.trim().toLowerCase();
      if (q && !(s.name || '').toLowerCase().includes(q)) return false;
    }
    if (filterType        && s.type        !== filterType)        return false;
    if (filterCriticality && s.criticality !== filterCriticality) return false;
    if (filterStatus      && s.status      !== filterStatus)      return false;
    if (filterExpiry && expiryBucket(s.contractExpiry) !== filterExpiry) return false;
    return true;
  });

  const selectedInView = filtered.filter((s) => selectedIds.has(s.id));
  const allChecked     = filtered.length > 0 && selectedInView.length === filtered.length;
  const someChecked    = selectedInView.length > 0;

  const toggleAll = (checked) => {
    if (checked) setSelectedIds(new Set(filtered.map((s) => s.id)));
    else         setSelectedIds(new Set());
  };

  const toggleOne = (id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  const handleStatusChange = async (supplier, newStatus) => {
    const id      = supplier.id;
    const updated = { ...supplier, status: newStatus, updatedAt: new Date().toISOString() };
    updated.changeLog = appendChangeLog(supplier, updated, org.consultantPersonId || 'system');
    setSavingId(id);
    let succeeded = false;
    try {
      await writeOrgFile(org.id, `suppliers/${id}.json`, updated);
      onSuppliersChanged();
      succeeded = true;
      setSavingId(id + '-ok');
      setTimeout(() => setSavingId(prev => prev === id + '-ok' ? null : prev), 1500);
    } catch {
      showError(t.saveError);
    } finally {
      if (!succeeded) setSavingId(null);
    }
  };

  const renderBlockContent = (hits) => (
    <div>
      <h3 style={{ ...FONT_BODY, fontWeight: 700, color: TEXT_HEADING, margin: '0 0 0.5rem' }}>
        {t.deleteBlockedTitle}
      </h3>
      <p style={{ ...FONT_BODY, color: TEXT_DEFAULT, margin: '0 0 0.75rem' }}>
        {t.deleteBlockedReferencedBy} {summariseHits(hits, t)}
      </p>
      <div style={{ maxHeight: '40vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '1.25rem' }}>
        {hits.map((h, i) => (
          <button
            key={i}
            type="button"
            onClick={() => { setConfirmState(null); onNavigate?.(HOLDER_NAV[h.holderType], null); }}
            style={{ ...BTN_SECONDARY, ...FONT_LABEL, borderRadius: 6, padding: '0.35rem 0.6rem', textAlign: 'left', cursor: 'pointer' }}
          >
            {`${holderLabel(t, h.holderType, 1)} ${h.holderRef}`}
          </button>
        ))}
      </div>
    </div>
  );

  const renderBulkContent = (allBlocked, deletableCount, blockedCount, total, blockedSummaries) => (
    <div>
      <h3 style={{ ...FONT_BODY, fontWeight: 700, color: TEXT_HEADING, margin: '0 0 0.5rem' }}>
        {t.deleteBlockedTitle}
      </h3>
      <p style={{ ...FONT_BODY, color: TEXT_DEFAULT, margin: '0 0 0.75rem' }}>
        {allBlocked
          ? t.bulkDeleteSummary_allBlocked
          : t.bulkDeleteSummary_mixed
              .replace('{deletable}', String(deletableCount))
              .replace('{blocked}',   String(blockedCount))
              .replace('{total}',     String(total))}
      </p>
      <div style={{ maxHeight: '40vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '1.25rem' }}>
        {blockedSummaries.map((row, i) => (
          <div key={i} style={{ ...FONT_LABEL, color: TEXT_DEFAULT, padding: '0.3rem 0.5rem', border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, background: BG_SURFACE }}>
            <span style={{ fontWeight: 600 }}>{row.label}</span>
            <span style={{ color: TEXT_MUTED }}>{` — ${t.deleteBlockedReferencedBy} ${row.summary}`}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const handleDeleteOne = async (supplier) => {
    if (scanningDelete) return;
    setScanningDelete(true);
    let hits = [];
    try { hits = await scanReferencesToEntity(org.id, 'supplier', supplier.id); }
    catch { hits = []; }
    setScanningDelete(false);
    if (hits.length === 0) {
      const msg = t.deleteConfirm.replace('{name}', supplier.name);
      setConfirmState({
        message: msg,
        confirmLabel: t.deleteBtn,
        onConfirm: async () => {
          setConfirmState(null);
          try {
            await deleteOrgFile(org.id, `suppliers/${supplier.id}.json`);
            setSelectedIds((prev) => { const next = new Set(prev); next.delete(supplier.id); return next; });
            onSuppliersChanged();
          } catch { showError(t.saveError); }
        },
      });
      return;
    }
    setConfirmState({
      content: renderBlockContent(hits),
      confirmLabel: t.formCancel,
      confirmColor: BTN_SECONDARY,
      onConfirm: () => setConfirmState(null),
    });
  };

  const handleDeleteSelected = async () => {
    if (!selectedInView.length) return;
    if (scanningDelete) return;
    setScanningDelete(true);
    const items = [...selectedInView];
    let hitsLists;
    try {
      hitsLists = await Promise.all(items.map(s => scanReferencesToEntity(org.id, 'supplier', s.id)));
    } catch {
      hitsLists = items.map(() => []);
    }
    setScanningDelete(false);
    const deletable = [];
    const blocked   = [];
    items.forEach((s, i) => {
      if ((hitsLists[i] || []).length === 0) deletable.push(s);
      else blocked.push({ record: s, hits: hitsLists[i] });
    });
    const performBulkDelete = async (cohort) => {
      await Promise.all(cohort.map((s) => deleteOrgFile(org.id, `suppliers/${s.id}.json`).catch(() => {})));
      setSelectedIds(new Set());
      onSuppliersChanged();
    };
    if (blocked.length === 0) {
      const names = items.map((s) => s.name).join(', ');
      setConfirmState({
        message: t.deleteSelectedConfirm.replace('{names}', names),
        confirmLabel: t.deleteBtn,
        onConfirm: async () => {
          setConfirmState(null);
          await performBulkDelete(items);
        },
      });
      return;
    }
    const blockedSummaries = blocked.map(({ record, hits }) => ({
      label: record.name || record.ref || record.id,
      summary: summariseHits(hits, t),
    }));
    const allBlocked = deletable.length === 0;
    setConfirmState({
      content: renderBulkContent(allBlocked, deletable.length, blocked.length, items.length, blockedSummaries),
      confirmLabel: allBlocked ? t.formCancel : t.continueWithDeletables,
      confirmColor: allBlocked ? BTN_SECONDARY : BTN_DESTRUCTIVE_FILLED,
      onConfirm: async () => {
        setConfirmState(null);
        if (allBlocked) return;
        await performBulkDelete(deletable);
      },
    });
  };

  function getStatusAction(status) {
    if (status === 'Active')       return { label: t.flagReviewBtn, newStatus: 'Under Review' };
    if (status === 'Under Review') return { label: t.markActiveBtn, newStatus: 'Active' };
    if (status === 'Terminated')   return { label: t.reactivateBtn, newStatus: 'Active' };
    return null;
  }

  // ── Styles ──────────────────────────────────────────────────────────────────
  const thSt = {
    ...FONT_TINY, color: TEXT_DISABLED,
    letterSpacing: '0.04em', textTransform: 'uppercase',
    padding: '0.4rem 0.75rem', borderBottom: `1px solid ${BORDER_DEFAULT}`,
    fontWeight: 600, textAlign: 'left', background: BG_CONTENT, whiteSpace: 'nowrap',
  };
  const tdSt = {
    ...FONT_LIST_ITEM,
    padding: '0.55rem 0.75rem', borderBottom: `1px solid ${BG_SHELL}`,
    verticalAlign: 'middle',
  };
  const filterSelectSt = {
    ...FONT_SMALL,
    border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 5,
    padding: '0.3rem 0.5rem', background: SURFACE_WHITE, color: TEXT_DEFAULT, cursor: 'pointer',
  };
  const btnSmall = {
    ...BTN_SECONDARY,
    ...FONT_TINY,
    borderRadius: 5,
    padding: '0.2rem 0.5rem', cursor: 'pointer',
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div>
      {listError && (
        <div style={{ ...FONT_LABEL, marginBottom: '0.75rem', padding: '0.6rem 1rem', background: ERROR_BG, border: `1px solid ${ERROR_BORDER}`, borderRadius: 8, color: ERROR_TEXT }}>
          {listError}
        </div>
      )}
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-start', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterName}</span>
            <input type="text" value={filterText} onChange={(e) => setFilterText(e.target.value)} placeholder={t.filterNamePh}
              style={{ ...filterSelectSt, minWidth: 180, cursor: 'text' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterType}</span>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)} style={filterSelectSt}>
              <option value="">{t.filterAll}</option>
              {SUPPLIER_TYPES.map((v) => <option key={v} value={v}>{t.typeLabels[v]}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterCriticality}</span>
            <select value={filterCriticality} onChange={(e) => setFilterCriticality(e.target.value)} style={filterSelectSt}>
              <option value="">{t.filterAll}</option>
              {CRITICALITIES.map((v) => <option key={v} value={v}>{t.criticalityLabels[v]}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterStatus}</span>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={filterSelectSt}>
              <option value="">{t.filterAll}</option>
              {SUPPLIER_STATUSES.map((v) => <option key={v} value={v}>{t.statusLabels[v]}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterExpiry}</span>
            <select value={filterExpiry} onChange={(e) => setFilterExpiry(e.target.value)} style={filterSelectSt}>
              <option value="">{t.expiryAll}</option>
              <option value="expired">{t.expiryExpired}</option>
              <option value="soon">{t.expirySoon}</option>
              <option value="active">{t.expiryActive}</option>
              <option value="none">{t.expiryNone}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {someChecked && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0.75rem', marginBottom: '0.6rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8 }}>
          <span style={{ ...FONT_LABEL, color: BRAND_PRIMARY_DARK }}>
            {t.nSelected.replace('{n}', selectedInView.length)}
          </span>
          <button onClick={handleDeleteSelected} style={{ ...btnSmall, border: BTN_DESTRUCTIVE_OUTLINE.border, color: BTN_DESTRUCTIVE_OUTLINE.color }}>
            {t.deleteSelected}
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <p style={{ ...FONT_LABEL, color: TEXT_MUTED }}>{t.loading}</p>
      )}

      {/* Empty state */}
      {!loading && filtered.length === 0 && (
        <div style={{ background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 10, padding: '2.5rem', textAlign: 'center' }}>
          <p style={{ ...FONT_BODY, color: TEXT_MUTED, margin: '0 0 0.5rem' }}>{t.emptyTitle}</p>
          <p style={{ ...FONT_LABEL, color: TEXT_DISABLED, margin: 0 }}>{t.emptyHint}</p>
        </div>
      )}

      {/* Table */}
      {!loading && filtered.length > 0 && (
        <div style={{ overflowX: 'auto', borderRadius: 10, border: `1px solid ${BORDER_DEFAULT}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: BG_SURFACE }}>
            <thead>
              <tr>
                <th style={{ ...thSt, width: 36, padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    title={t.selectAll}
                    checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked; }}
                    onChange={(e) => toggleAll(e.target.checked)}
                  />
                </th>
                <th style={thSt}>{t.colRef}</th>
                <th style={{ ...thSt, minWidth: 160 }}>{t.colName}</th>
                <th style={thSt}>{t.colType}</th>
                <th style={thSt}>{t.colCriticality}</th>
                <th style={{ ...thSt, minWidth: 120 }}>{t.colContractExpiry}</th>
                <th style={thSt}>{t.colStatus}</th>
                <th style={thSt}>{t.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s, idx) => {
                const action = getStatusAction(s.status);
                const expStyle = expiryStyle(s.contractExpiry);
                return (
                  <tr key={s.id} style={{ background: idx % 2 === 0 ? BG_SURFACE : 'transparent' }}>
                    <td style={{ ...tdSt, textAlign: 'center', padding: '0.55rem 0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(s.id)}
                        onChange={(e) => toggleOne(s.id, e.target.checked)}
                      />
                    </td>
                    <td style={{ ...tdSt, ...FONT_LABEL, color: TEXT_MUTED, whiteSpace: 'nowrap' }}>
                      {s.ref || s.id}
                    </td>
                    <td style={{ ...tdSt, fontWeight: 600, color: TEXT_HEADING }}>{s.name}</td>
                    <td style={{ ...tdSt, ...FONT_LABEL, color: TEXT_MUTED }}>{t.typeLabels[s.type] || s.type}</td>
                    <td style={tdSt}><Badge label={s.criticality} displayLabel={t.criticalityLabels[s.criticality] || s.criticality} colorMap={CRITICALITY_COLORS} /></td>
                    <td style={{ ...tdSt, ...FONT_LABEL, ...expStyle }}>
                      {s.contractExpiry || t.noExpiry}
                    </td>
                    <td style={tdSt}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        <Badge label={s.status} displayLabel={t.statusLabels[s.status] || s.status} colorMap={STATUS_COLORS} />
                        {(() => {
                          const ds = dateStatus(s.nextReviewDate);
                          if (!ds) return null;
                          return (
                            <span style={{
                              ...FONT_TINY,
                              fontWeight: 600,
                              background: ds === 'overdue' ? STATUS_BAD.bg : STATUS_WARN.bg,
                              color:      ds === 'overdue' ? STATUS_BAD.text : STATUS_WARN.text,
                              borderRadius: 4,
                              padding: '0.15rem 0.45rem',
                              marginLeft: '0.35rem',
                              whiteSpace: 'nowrap',
                            }}>
                              {ds === 'overdue' ? t.badgeOverdue : t.badgeSoon}
                            </span>
                          );
                        })()}
                      </div>
                    </td>
                    <td style={{ ...tdSt, whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: '0.35rem' }}>
                        <button onClick={() => onView(s)} style={btnSmall}>{t.viewBtn}</button>
                        <button onClick={() => onEdit(s)} style={btnSmall}>{t.editBtn}</button>
                        {action && savingId === s.id + '-ok' && (
                          <span style={{ fontSize: '0.78rem', color: BRAND_PRIMARY_DARK, fontWeight: 600, padding: '0.2rem 0.4rem' }}>✓</span>
                        )}
                        {action && savingId !== s.id + '-ok' && (
                          <button
                            onClick={() => handleStatusChange(s, action.newStatus)}
                            disabled={savingId === s.id}
                            style={{
                              ...btnSmall,
                              border: s.status === 'Active' ? BTN_STATUS_FLAG.border : BTN_STATUS_FORWARD.border,
                              color: s.status === 'Active' ? BTN_STATUS_FLAG.color : BTN_STATUS_FORWARD.color,
                              opacity: savingId === s.id ? 0.6 : 1,
                              cursor:  savingId === s.id ? 'default' : 'pointer',
                            }}
                          >
                            {savingId === s.id ? t.saving : action.label}
                          </button>
                        )}
                        <button onClick={() => handleDeleteOne(s)} style={{ ...btnSmall, border: BTN_DESTRUCTIVE_OUTLINE.border, color: BTN_DESTRUCTIVE_OUTLINE.color }}>
                          {t.deleteBtn}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <ConfirmDialog
        open={confirmState !== null}
        message={confirmState?.message}
        content={confirmState?.content}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
        cancelLabel={t.formCancel || 'Cancel'}
        confirmColor={confirmState?.confirmColor}
        secondaryAction={confirmState?.secondaryAction}
      />
    </div>
  );
}
